import {
  LAYOUT_TEMPLATE_PATCH_FIELDS_BY_AI_POLICY_MODE,
  LAYOUT_TEMPLATE_PATCH_FIELDS_BY_BLOCK_TYPE,
  socialPostLayoutTemplateDefinitionSchema,
  socialPostLayoutTemplateIdSchema,
  type SocialPostLayoutTemplateAiPolicyV2,
  type SocialPostLayoutTemplateDefinitionV2,
  type SocialPostLayoutTemplateLayerV2,
  type SocialPostLayoutTemplatePatchField,
  type SocialPostType,
} from "@runcheetah/shared";

export interface LayoutTemplateOutputLike {
  id: string;
  templateId: string;
  name: string;
  description: string;
  status: SocialPostLayoutTemplateDefinitionV2["status"];
  draftTemplate: SocialPostLayoutTemplateDefinitionV2;
  publishedTemplate: SocialPostLayoutTemplateDefinitionV2 | null;
  publishedRevisionNumber: number | null;
  hasUnpublishedChanges: boolean;
  sortOrder: number;
}

export interface LayoutTemplateDraft {
  id: string | null;
  templateId: string;
  name: string;
  description: string;
  status: SocialPostLayoutTemplateDefinitionV2["status"];
  bestForText: string;
  avoidWhenText: string;
  sortOrder: number;
  template: SocialPostLayoutTemplateDefinitionV2;
  slotKeys: LayoutTemplateSlotKeys;
}

export interface LayoutTemplateSlotKeys {
  imageSlots: string[];
  textSlots: string[];
}

export type LayoutTemplateWriteInput = {
  template: SocialPostLayoutTemplateDefinitionV2;
  sortOrder: number;
};

export type LayoutTemplateRawJsonParseResult =
  | { valid: true; draft: LayoutTemplateDraft }
  | { valid: false; error: string };

export type LayoutTemplateImageSlot =
  SocialPostLayoutTemplateDefinitionV2["slots"]["imageSlots"][number];
export type LayoutTemplateTextSlot =
  SocialPostLayoutTemplateDefinitionV2["slots"]["textSlots"][number];
export type LayoutTemplateLogoSlot = SocialPostLayoutTemplateDefinitionV2["slots"]["logoSlot"];
export type LayoutTemplateLayerBinding = SocialPostLayoutTemplateLayerV2["binding"];
export type LayoutTemplateAiPolicy = SocialPostLayoutTemplateAiPolicyV2;
export type LayoutTemplatePatchField = SocialPostLayoutTemplatePatchField;
export type LayoutTemplateSupportedPostType = Extract<
  SocialPostType,
  "feed_portrait" | "feed_square" | "story" | "tiktok_portrait" | "tiktok_carousel_portrait"
>;

export interface LayoutTemplateLayerEntry {
  id: string;
  label: string;
  type: SocialPostLayoutTemplateLayerV2["type"];
  zIndex: number;
  visible: boolean;
}

export interface LayoutTemplateLayerFrameUpdate {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
}

export type LayoutTemplateTextLayerStyleUpdate = Partial<
  Pick<Extract<SocialPostLayoutTemplateLayerV2, { type: "text" }>, "stroke" | "strokeWidth">
>;

export interface LayoutTemplatePasteboardFrame {
  canvasX: number;
  canvasY: number;
  stageWidth: number;
  stageHeight: number;
}

export type LayoutTemplateLayerMoveDirection = "forward" | "backward" | "front" | "back";

export const LAYOUT_TEMPLATE_SUPPORTED_POST_TYPES = [
  "feed_portrait",
  "feed_square",
  "story",
  "tiktok_portrait",
  "tiktok_carousel_portrait",
] as const satisfies readonly LayoutTemplateSupportedPostType[];

export const REQUIRED_LAYOUT_TEMPLATE_POST_TYPE = "feed_portrait" as const;

const splitLines = (value: string): string[] =>
  value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const formatLines = (items: readonly string[]): string => items.join("\n");

const validateSortOrder = (sortOrder: number): string[] => {
  if (!Number.isInteger(sortOrder)) {
    return ["Sort order must be a whole number."];
  }

  if (sortOrder < 0) {
    return ["Sort order cannot be negative."];
  }

  if (sortOrder > 10_000) {
    return ["Sort order must be 10000 or lower."];
  }

  return [];
};

const formatValidationIssuePath = (path: readonly PropertyKey[]): string =>
  path.length > 0 ? path.map(String).join(".") : "template";

const formatValidationIssues = (
  issues: readonly { path: readonly PropertyKey[]; message: string }[],
): string =>
  issues.map((issue) => `${formatValidationIssuePath(issue.path)}: ${issue.message}`).join("; ");

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const resolveRawJsonPayload = (
  value: unknown,
  fallbackSortOrder: number,
): { template: unknown; sortOrder: unknown } => {
  if (isRecord(value) && "template" in value) {
    return {
      template: value.template,
      sortOrder: value.sortOrder ?? fallbackSortOrder,
    };
  }

  return { template: value, sortOrder: fallbackSortOrder };
};

const sortLayersBackToFront = (
  left: SocialPostLayoutTemplateLayerV2,
  right: SocialPostLayoutTemplateLayerV2,
) => left.zIndex - right.zIndex;

const sortLayersFrontToBack = (
  left: SocialPostLayoutTemplateLayerV2,
  right: SocialPostLayoutTemplateLayerV2,
) => right.zIndex - left.zIndex;

const moveArrayItemToIndex = <T>(items: readonly T[], fromIndex: number, toIndex: number): T[] => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return [...items];
  }

  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  if (item === undefined) {
    return [...items];
  }

  next.splice(toIndex, 0, item);
  return next;
};

const snapLayoutTemplatePixel = (value: number): number => Math.round(value);

const createSlotKey = (prefix: string, slotId: string, index: number): string =>
  `${prefix}:${slotId}:${index}`;

const createSlotKeys = (
  template: SocialPostLayoutTemplateDefinitionV2,
): LayoutTemplateSlotKeys => ({
  imageSlots: template.slots.imageSlots.map((slot, index) =>
    createSlotKey("image", slot.id, index),
  ),
  textSlots: template.slots.textSlots.map((slot, index) => createSlotKey("text", slot.id, index)),
});

export const createLayoutTemplateId = (): string => globalThis.crypto.randomUUID();

const createBlankTemplate = (
  templateId: string,
  name: string,
): SocialPostLayoutTemplateDefinitionV2 =>
  socialPostLayoutTemplateDefinitionSchema.parse({
    type: "social_post.layout_template",
    version: 2,
    id: templateId,
    name,
    description: "Admin-authored layout template draft.",
    status: "draft",
    canonicalPostType: "feed_portrait",
    supportedPostTypes: ["feed_portrait", "feed_square"],
    selection: {
      bestFor: ["Custom marketing layouts"],
      avoidWhen: ["Required content slots have not been configured"],
    },
    slots: {
      imageSlots: [
        {
          id: "hero_image",
          label: "Hero image",
          kind: "hero",
          requirement: "unsupported",
          minCount: 0,
          maxCount: 0,
          purpose: "Enable and bind this slot when the template needs generated imagery.",
          safeZoneRole: "none",
        },
      ],
      textSlots: [
        {
          id: "headline",
          label: "Headline",
          kind: "headline",
          requirement: "unsupported",
          purpose: "Enable and bind this slot when the template needs a headline.",
          suggestedMaxLength: 80,
        },
      ],
      logoSlot: {
        requirement: "unsupported",
        purpose: "Enable and bind this slot when the template should show a brand logo.",
        preferredPlacements: [],
      },
    },
    canonicalScene: {
      canvas: { width: 1080, height: 1350, backgroundFill: "#f8fafc" },
      fonts: [],
      layers: [
        {
          id: "background",
          type: "rect",
          x: 0,
          y: 0,
          width: 1080,
          height: 1350,
          rotation: 0,
          opacity: 1,
          zIndex: 0,
          visible: true,
          fill: "#f8fafc",
          strokeWidth: 0,
          cornerRadius: 0,
          binding: { type: "static" },
          optional: false,
          aiPolicy: { mode: "locked", allowedFields: [] },
        },
      ],
    },
    previewFill: {
      text: {},
      images: {},
      controls: {},
      includeLogo: false,
    },
  });

export const createEmptyLayoutTemplateDraft = (
  existingTemplateIds: readonly string[] = [],
  sortOrder = 0,
): LayoutTemplateDraft => {
  const name = `New Layout Template ${existingTemplateIds.length + 1}`;
  const templateId = createLayoutTemplateId();
  const template = createBlankTemplate(templateId, name);

  return createLayoutTemplateDraftFromTemplate({
    id: null,
    template,
    status: "draft",
    sortOrder,
  });
};

export const createLayoutTemplateDraftFromTemplate = (input: {
  id: string | null;
  template: SocialPostLayoutTemplateDefinitionV2;
  status: SocialPostLayoutTemplateDefinitionV2["status"];
  sortOrder: number;
}): LayoutTemplateDraft => ({
  id: input.id,
  templateId: input.template.id,
  name: input.template.name,
  description: input.template.description,
  status: input.status,
  bestForText: formatLines(input.template.selection.bestFor),
  avoidWhenText: formatLines(input.template.selection.avoidWhen),
  sortOrder: input.sortOrder,
  template: input.template,
  slotKeys: createSlotKeys(input.template),
});

export const createLayoutTemplateDraftFromOutput = (
  output: LayoutTemplateOutputLike,
): LayoutTemplateDraft =>
  createLayoutTemplateDraftFromTemplate({
    id: output.id,
    template: output.draftTemplate,
    status: output.status,
    sortOrder: output.sortOrder,
  });

export const resolveLayoutTemplateDraftAfterDelete = (input: {
  currentDraft: LayoutTemplateDraft | null;
  deletedTemplateId: string;
  templates: readonly LayoutTemplateOutputLike[];
}): LayoutTemplateDraft | null => {
  if (input.currentDraft?.id !== input.deletedTemplateId) {
    return input.currentDraft;
  }

  const nextTemplate = input.templates.find((template) => template.id !== input.deletedTemplateId);
  return nextTemplate ? createLayoutTemplateDraftFromOutput(nextTemplate) : null;
};

export const reconcileLayoutTemplateDraftWithTemplates = (input: {
  currentDraft: LayoutTemplateDraft | null;
  templates: readonly LayoutTemplateOutputLike[];
}): LayoutTemplateDraft | null => {
  if (!input.currentDraft) {
    const firstTemplate = input.templates[0];
    return firstTemplate ? createLayoutTemplateDraftFromOutput(firstTemplate) : null;
  }

  if (!input.currentDraft.id) {
    return input.currentDraft;
  }

  if (input.templates.some((template) => template.id === input.currentDraft?.id)) {
    return input.currentDraft;
  }

  const firstTemplate = input.templates[0];
  return firstTemplate ? createLayoutTemplateDraftFromOutput(firstTemplate) : null;
};

const toLayoutTemplateDraftTemplateJson = (
  draft: LayoutTemplateDraft,
): SocialPostLayoutTemplateDefinitionV2 => ({
  ...draft.template,
  id: draft.templateId.trim(),
  name: draft.name.trim(),
  description: draft.description.trim(),
  status: draft.status,
  selection: {
    bestFor: splitLines(draft.bestForText),
    avoidWhen: splitLines(draft.avoidWhenText),
  },
});

export const buildLayoutTemplateWriteInput = (
  draft: LayoutTemplateDraft,
): LayoutTemplateWriteInput => {
  const template = socialPostLayoutTemplateDefinitionSchema.parse(
    toLayoutTemplateDraftTemplateJson(draft),
  );

  return {
    template,
    sortOrder: draft.sortOrder,
  };
};

export const formatLayoutTemplateRawJsonDraft = (draft: LayoutTemplateDraft): string =>
  `${JSON.stringify(
    {
      template: toLayoutTemplateDraftTemplateJson(draft),
      sortOrder: draft.sortOrder,
    },
    null,
    2,
  )}\n`;

export const parseLayoutTemplateRawJsonDraft = (
  rawJson: string,
  currentDraft: LayoutTemplateDraft,
): LayoutTemplateRawJsonParseResult => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson) as unknown;
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : "Raw JSON could not be parsed.",
    };
  }

  const payload = resolveRawJsonPayload(parsed, currentDraft.sortOrder);
  if (typeof payload.sortOrder !== "number") {
    return { valid: false, error: "Sort order must be a number." };
  }

  const sortOrderErrors = validateSortOrder(payload.sortOrder);
  if (sortOrderErrors.length > 0) {
    return { valid: false, error: sortOrderErrors.join(" ") };
  }

  const templateResult = socialPostLayoutTemplateDefinitionSchema.safeParse(payload.template);
  if (!templateResult.success) {
    return {
      valid: false,
      error: formatValidationIssues(templateResult.error.issues),
    };
  }

  if (currentDraft.id && templateResult.data.id !== currentDraft.templateId.trim()) {
    return {
      valid: false,
      error: "Template ID cannot be changed for an existing layout template.",
    };
  }

  return {
    valid: true,
    draft: createLayoutTemplateDraftFromTemplate({
      id: currentDraft.id,
      template: { ...templateResult.data, status: currentDraft.status },
      status: currentDraft.status,
      sortOrder: payload.sortOrder,
    }),
  };
};

export const validateLayoutTemplateDraft = (draft: LayoutTemplateDraft): string[] => {
  const templateIdResult = socialPostLayoutTemplateIdSchema.safeParse(draft.templateId.trim());
  const issues = templateIdResult.success
    ? []
    : templateIdResult.error.issues.map((issue) => issue.message);
  issues.push(...validateSortOrder(draft.sortOrder));

  const result = socialPostLayoutTemplateDefinitionSchema.safeParse(
    toLayoutTemplateDraftTemplateJson(draft),
  );

  if (!result.success) {
    issues.push(...result.error.issues.map((issue) => issue.message));
  }

  return Array.from(new Set(issues));
};

export const moveLayoutTemplateId = (ids: string[], id: string, direction: -1 | 1): string[] => {
  const index = ids.indexOf(id);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) {
    return ids;
  }

  const next = [...ids];
  const item = next[index]!;
  next[index] = next[nextIndex]!;
  next[nextIndex] = item;
  return next;
};

export const getNextLayoutTemplateSortOrder = (templates: LayoutTemplateOutputLike[]): number =>
  templates.reduce((max, template) => Math.max(max, template.sortOrder), 0) + 10;

export const getLayoutTemplateStatusLabel = (
  status: SocialPostLayoutTemplateDefinitionV2["status"],
): string => {
  if (status === "published") {
    return "Published";
  }

  if (status === "archived") {
    return "Archived";
  }

  return "Draft";
};

const getNextSlotId = (prefix: string, existingIds: readonly string[]): string => {
  let index = existingIds.length + 1;
  let id = `${prefix}_${index}`;
  while (existingIds.includes(id)) {
    index += 1;
    id = `${prefix}_${index}`;
  }

  return id;
};

const getNextSlotKey = (
  prefix: string,
  slotId: string,
  existingKeys: readonly string[],
): string => {
  let index = existingKeys.length;
  let key = createSlotKey(prefix, slotId, index);
  while (existingKeys.includes(key)) {
    index += 1;
    key = createSlotKey(prefix, slotId, index);
  }

  return key;
};

const withTemplate = (
  draft: LayoutTemplateDraft,
  template: SocialPostLayoutTemplateDefinitionV2,
): LayoutTemplateDraft => ({
  ...draft,
  template,
});

const normalizeLayoutTemplateSupportedPostTypes = (
  postTypes: readonly SocialPostType[],
): SocialPostType[] => {
  const selected = new Set<SocialPostType>([REQUIRED_LAYOUT_TEMPLATE_POST_TYPE, ...postTypes]);
  const editablePostTypes = LAYOUT_TEMPLATE_SUPPORTED_POST_TYPES.filter((postType) =>
    selected.has(postType),
  );
  const preservedPostTypes = postTypes.filter(
    (postType) => !LAYOUT_TEMPLATE_SUPPORTED_POST_TYPES.some((editable) => editable === postType),
  );

  return Array.from(new Set<SocialPostType>([...editablePostTypes, ...preservedPostTypes]));
};

export const updateLayoutTemplateSupportedPostType = (
  draft: LayoutTemplateDraft,
  postType: LayoutTemplateSupportedPostType,
  enabled: boolean,
): LayoutTemplateDraft => {
  const supportedPostTypes = enabled
    ? [...draft.template.supportedPostTypes, postType]
    : draft.template.supportedPostTypes.filter((item) => item !== postType);

  return withTemplate(draft, {
    ...draft.template,
    supportedPostTypes: normalizeLayoutTemplateSupportedPostTypes(supportedPostTypes),
  });
};

export const getLayoutTemplateLayerLabel = (layer: SocialPostLayoutTemplateLayerV2): string =>
  layer.layerName?.trim() || layer.id;

export const getSortedLayoutTemplateLayerEntries = (
  draft: LayoutTemplateDraft,
): LayoutTemplateLayerEntry[] =>
  draft.template.canonicalScene.layers.toSorted(sortLayersFrontToBack).map((layer) => ({
    id: layer.id,
    label: getLayoutTemplateLayerLabel(layer),
    type: layer.type,
    zIndex: layer.zIndex,
    visible: layer.visible,
  }));

export const updateLayoutTemplateLayerFrame = (
  draft: LayoutTemplateDraft,
  layerId: string,
  changes: LayoutTemplateLayerFrameUpdate,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers.map((layer) =>
        layer.id === layerId ? { ...layer, ...changes } : layer,
      ),
    },
  });

export const updateLayoutTemplateLayerText = (
  draft: LayoutTemplateDraft,
  layerId: string,
  text: string,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers.map((layer) =>
        layer.id === layerId && layer.type === "text" ? { ...layer, text } : layer,
      ),
    },
  });

export const updateLayoutTemplateTextLayerStyle = (
  draft: LayoutTemplateDraft,
  layerId: string,
  changes: LayoutTemplateTextLayerStyleUpdate,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers.map((layer) =>
        layer.id === layerId && layer.type === "text" ? { ...layer, ...changes } : layer,
      ),
    },
  });

export const moveLayoutTemplateLayer = (
  draft: LayoutTemplateDraft,
  layerId: string,
  direction: LayoutTemplateLayerMoveDirection,
): LayoutTemplateDraft => {
  const frontToBackLayers = draft.template.canonicalScene.layers.toSorted(sortLayersFrontToBack);
  const currentIndex = frontToBackLayers.findIndex((layer) => layer.id === layerId);
  if (currentIndex < 0) {
    return draft;
  }

  const nextIndex = (() => {
    if (direction === "front") {
      return 0;
    }

    if (direction === "back") {
      return frontToBackLayers.length - 1;
    }

    return direction === "forward" ? currentIndex - 1 : currentIndex + 1;
  })();
  if (nextIndex < 0 || nextIndex >= frontToBackLayers.length || nextIndex === currentIndex) {
    return draft;
  }

  const reorderedLayers = moveArrayItemToIndex(frontToBackLayers, currentIndex, nextIndex);
  const nextZIndexByLayerId = new Map(
    reorderedLayers.map((layer, index) => [layer.id, reorderedLayers.length - 1 - index] as const),
  );

  return withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers
        .map((layer) => ({ ...layer, zIndex: nextZIndexByLayerId.get(layer.id) ?? layer.zIndex }))
        .toSorted(sortLayersBackToFront),
    },
  });
};

export const resolveLayoutTemplateLayerDragFrame = (input: {
  dragX: number;
  dragY: number;
  scale: number;
}): Pick<LayoutTemplateLayerFrameUpdate, "x" | "y"> => ({
  x: snapLayoutTemplatePixel(input.dragX / input.scale),
  y: snapLayoutTemplatePixel(input.dragY / input.scale),
});

export const resolveLayoutTemplateLayerTransformFrame = (input: {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  scale: number;
}): Required<LayoutTemplateLayerFrameUpdate> => ({
  x: snapLayoutTemplatePixel(input.x / input.scale),
  y: snapLayoutTemplatePixel(input.y / input.scale),
  width: Math.max(8, snapLayoutTemplatePixel(input.width / input.scale)),
  height: Math.max(8, snapLayoutTemplatePixel(input.height / input.scale)),
  rotation: input.rotation,
});

export const resolveLayoutTemplateLayerNodeTransformFrame = (input: {
  x: number;
  y: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  scale: number;
}): Required<LayoutTemplateLayerFrameUpdate> =>
  resolveLayoutTemplateLayerTransformFrame({
    x: input.x,
    y: input.y,
    width: input.width * input.scaleX,
    height: input.height * input.scaleY,
    rotation: input.rotation,
    scale: input.scale,
  });

export const resolveLayoutTemplatePasteboardFrame = (input: {
  canvas: { width: number; height: number };
  layers: readonly Pick<SocialPostLayoutTemplateLayerV2, "height" | "width" | "x" | "y">[];
  padding: number;
  scale: number;
}): LayoutTemplatePasteboardFrame => {
  const minLayerX = Math.min(0, ...input.layers.map((layer) => layer.x));
  const minLayerY = Math.min(0, ...input.layers.map((layer) => layer.y));
  const maxLayerX = Math.max(
    input.canvas.width,
    ...input.layers.map((layer) => layer.x + layer.width),
  );
  const maxLayerY = Math.max(
    input.canvas.height,
    ...input.layers.map((layer) => layer.y + layer.height),
  );
  const leftOverflow = Math.max(0, -minLayerX);
  const topOverflow = Math.max(0, -minLayerY);
  const rightOverflow = Math.max(0, maxLayerX - input.canvas.width);
  const bottomOverflow = Math.max(0, maxLayerY - input.canvas.height);

  return {
    canvasX: snapLayoutTemplatePixel((input.padding + leftOverflow) * input.scale),
    canvasY: snapLayoutTemplatePixel((input.padding + topOverflow) * input.scale),
    stageWidth: snapLayoutTemplatePixel(
      (input.canvas.width + leftOverflow + rightOverflow + input.padding * 2) * input.scale,
    ),
    stageHeight: snapLayoutTemplatePixel(
      (input.canvas.height + topOverflow + bottomOverflow + input.padding * 2) * input.scale,
    ),
  };
};

export const isLayoutTemplateLayerOutsideCanvas = (
  layer: Pick<SocialPostLayoutTemplateLayerV2, "height" | "width" | "x" | "y">,
  canvas: { width: number; height: number },
): boolean =>
  layer.x + layer.width <= 0 ||
  layer.y + layer.height <= 0 ||
  layer.x >= canvas.width ||
  layer.y >= canvas.height;

export const resolveLayoutTemplateLayerCanvasRecoveryFrame = (
  layer: Pick<SocialPostLayoutTemplateLayerV2, "height" | "width" | "x" | "y">,
  canvas: { width: number; height: number },
): Pick<LayoutTemplateLayerFrameUpdate, "x" | "y"> => ({
  x: snapLayoutTemplatePixel(
    Math.min(Math.max(layer.x, 0), Math.max(0, canvas.width - layer.width)),
  ),
  y: snapLayoutTemplatePixel(
    Math.min(Math.max(layer.y, 0), Math.max(0, canvas.height - layer.height)),
  ),
});

const clearLayerBindingsForSlot = (
  layers: SocialPostLayoutTemplateLayerV2[],
  type: "textSlot" | "imageSlot",
  slotId: string,
): SocialPostLayoutTemplateLayerV2[] =>
  layers.map((layer) =>
    layer.binding.type === type && layer.binding.slotId === slotId
      ? { ...layer, binding: { type: "static" } }
      : layer,
  );

const renameLayerBindingsForSlot = (
  layers: SocialPostLayoutTemplateLayerV2[],
  type: "textSlot" | "imageSlot",
  oldSlotId: string,
  newSlotId: string,
): SocialPostLayoutTemplateLayerV2[] =>
  layers.map((layer) =>
    layer.binding.type === type && layer.binding.slotId === oldSlotId
      ? { ...layer, binding: { ...layer.binding, slotId: newSlotId } }
      : layer,
  );

const removeRecordKey = <TValue>(
  record: Record<string, TValue>,
  key: string,
): Record<string, TValue> => {
  const { [key]: _removed, ...rest } = record;
  return rest;
};

const renameRecordKey = <TValue>(
  record: Record<string, TValue>,
  oldKey: string,
  newKey: string,
): Record<string, TValue> => {
  if (!(oldKey in record)) {
    return record;
  }

  const value = record[oldKey]!;
  return { ...removeRecordKey(record, oldKey), [newKey]: value };
};

export const getLayoutTemplatePatchFieldsForLayer = (
  layer: Pick<SocialPostLayoutTemplateLayerV2, "type">,
  mode: LayoutTemplateAiPolicy["mode"],
): LayoutTemplatePatchField[] => {
  const modeFields = new Set<LayoutTemplatePatchField>(
    LAYOUT_TEMPLATE_PATCH_FIELDS_BY_AI_POLICY_MODE[mode],
  );

  return LAYOUT_TEMPLATE_PATCH_FIELDS_BY_BLOCK_TYPE[layer.type].filter((field) =>
    modeFields.has(field),
  );
};

const sanitizeLayoutTemplateAiPolicy = (
  layer: Pick<SocialPostLayoutTemplateLayerV2, "type" | "optional">,
  policy: LayoutTemplateAiPolicy,
): LayoutTemplateAiPolicy => {
  const allowedFields = new Set(getLayoutTemplatePatchFieldsForLayer(layer, policy.mode));
  const canPatchVisibility = layer.optional && policy.allowHideWhenOptional;

  return {
    ...policy,
    allowHideWhenOptional: layer.optional ? policy.allowHideWhenOptional : false,
    allowedFields: policy.allowedFields.filter(
      (field) => allowedFields.has(field) && (field !== "visible" || canPatchVisibility),
    ),
  };
};

export const createLayoutTemplateImageSlotDraft = (
  existingSlots: readonly LayoutTemplateImageSlot[],
): LayoutTemplateImageSlot => {
  const id = getNextSlotId(
    "image_slot",
    existingSlots.map((slot) => slot.id),
  );

  return {
    id,
    label: "Image slot",
    kind: "supporting",
    requirement: "optional",
    minCount: 0,
    maxCount: 1,
    purpose: "Generated image slot used by one or more image layers.",
    safeZoneRole: "none",
  };
};

export const createLayoutTemplateTextSlotDraft = (
  existingSlots: readonly LayoutTemplateTextSlot[],
): LayoutTemplateTextSlot => {
  const id = getNextSlotId(
    "text_slot",
    existingSlots.map((slot) => slot.id),
  );

  return {
    id,
    label: "Text slot",
    kind: "body",
    requirement: "optional",
    purpose: "Planner-filled text slot used by one or more text layers.",
    suggestedMaxLength: 120,
  };
};

export const addLayoutTemplateImageSlot = (draft: LayoutTemplateDraft): LayoutTemplateDraft => {
  const nextSlot = createLayoutTemplateImageSlotDraft(draft.template.slots.imageSlots);
  const imageSlots = [...draft.template.slots.imageSlots, nextSlot];

  return {
    ...withTemplate(draft, {
      ...draft.template,
      slots: { ...draft.template.slots, imageSlots },
    }),
    slotKeys: {
      ...draft.slotKeys,
      imageSlots: [
        ...draft.slotKeys.imageSlots,
        getNextSlotKey("image", nextSlot.id, draft.slotKeys.imageSlots),
      ],
    },
  };
};

export const addLayoutTemplateTextSlot = (draft: LayoutTemplateDraft): LayoutTemplateDraft => {
  const nextSlot = createLayoutTemplateTextSlotDraft(draft.template.slots.textSlots);
  const textSlots = [...draft.template.slots.textSlots, nextSlot];

  return {
    ...withTemplate(draft, {
      ...draft.template,
      slots: { ...draft.template.slots, textSlots },
    }),
    slotKeys: {
      ...draft.slotKeys,
      textSlots: [
        ...draft.slotKeys.textSlots,
        getNextSlotKey("text", nextSlot.id, draft.slotKeys.textSlots),
      ],
    },
  };
};

export const updateLayoutTemplateImageSlot = (
  draft: LayoutTemplateDraft,
  index: number,
  patch: Partial<LayoutTemplateImageSlot>,
): LayoutTemplateDraft => {
  const existing = draft.template.slots.imageSlots[index];
  if (!existing) {
    return draft;
  }

  const nextSlot = { ...existing, ...patch };
  const layers =
    patch.id && patch.id !== existing.id
      ? renameLayerBindingsForSlot(
          draft.template.canonicalScene.layers,
          "imageSlot",
          existing.id,
          patch.id,
        )
      : draft.template.canonicalScene.layers;

  return withTemplate(draft, {
    ...draft.template,
    slots: {
      ...draft.template.slots,
      imageSlots: draft.template.slots.imageSlots.map((slot, slotIndex) =>
        slotIndex === index ? nextSlot : slot,
      ),
    },
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers,
    },
    previewFill: {
      ...draft.template.previewFill,
      images:
        patch.id && patch.id !== existing.id
          ? renameRecordKey(draft.template.previewFill.images, existing.id, patch.id)
          : draft.template.previewFill.images,
    },
  });
};

export const updateLayoutTemplateTextSlot = (
  draft: LayoutTemplateDraft,
  index: number,
  patch: Partial<LayoutTemplateTextSlot>,
): LayoutTemplateDraft => {
  const existing = draft.template.slots.textSlots[index];
  if (!existing) {
    return draft;
  }

  const nextSlot = { ...existing, ...patch };
  const layers =
    patch.id && patch.id !== existing.id
      ? renameLayerBindingsForSlot(
          draft.template.canonicalScene.layers,
          "textSlot",
          existing.id,
          patch.id,
        )
      : draft.template.canonicalScene.layers;

  return withTemplate(draft, {
    ...draft.template,
    slots: {
      ...draft.template.slots,
      textSlots: draft.template.slots.textSlots.map((slot, slotIndex) =>
        slotIndex === index ? nextSlot : slot,
      ),
    },
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers,
    },
    previewFill: {
      ...draft.template.previewFill,
      text:
        patch.id && patch.id !== existing.id
          ? renameRecordKey(draft.template.previewFill.text, existing.id, patch.id)
          : draft.template.previewFill.text,
    },
  });
};

export const removeLayoutTemplateImageSlot = (
  draft: LayoutTemplateDraft,
  index: number,
): LayoutTemplateDraft => {
  const removed = draft.template.slots.imageSlots[index];
  if (!removed) {
    return draft;
  }

  return {
    ...withTemplate(draft, {
      ...draft.template,
      slots: {
        ...draft.template.slots,
        imageSlots: draft.template.slots.imageSlots.filter((_, slotIndex) => slotIndex !== index),
      },
      canonicalScene: {
        ...draft.template.canonicalScene,
        layers: clearLayerBindingsForSlot(
          draft.template.canonicalScene.layers,
          "imageSlot",
          removed.id,
        ),
      },
      previewFill: {
        ...draft.template.previewFill,
        images: removeRecordKey(draft.template.previewFill.images, removed.id),
      },
    }),
    slotKeys: {
      ...draft.slotKeys,
      imageSlots: draft.slotKeys.imageSlots.filter((_, slotIndex) => slotIndex !== index),
    },
  };
};

export const removeLayoutTemplateTextSlot = (
  draft: LayoutTemplateDraft,
  index: number,
): LayoutTemplateDraft => {
  const removed = draft.template.slots.textSlots[index];
  if (!removed) {
    return draft;
  }

  return {
    ...withTemplate(draft, {
      ...draft.template,
      slots: {
        ...draft.template.slots,
        textSlots: draft.template.slots.textSlots.filter((_, slotIndex) => slotIndex !== index),
      },
      canonicalScene: {
        ...draft.template.canonicalScene,
        layers: clearLayerBindingsForSlot(
          draft.template.canonicalScene.layers,
          "textSlot",
          removed.id,
        ),
      },
      previewFill: {
        ...draft.template.previewFill,
        text: removeRecordKey(draft.template.previewFill.text, removed.id),
      },
    }),
    slotKeys: {
      ...draft.slotKeys,
      textSlots: draft.slotKeys.textSlots.filter((_, slotIndex) => slotIndex !== index),
    },
  };
};

export const updateLayoutTemplateLogoSlot = (
  draft: LayoutTemplateDraft,
  patch: Partial<LayoutTemplateLogoSlot>,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    slots: {
      ...draft.template.slots,
      logoSlot: { ...draft.template.slots.logoSlot, ...patch },
    },
  });

export const updateLayoutTemplateLayerBinding = (
  draft: LayoutTemplateDraft,
  layerId: string,
  binding: LayoutTemplateLayerBinding,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers.map((layer) =>
        layer.id === layerId ? { ...layer, binding } : layer,
      ),
    },
  });

export const updateLayoutTemplateLayerAiPolicy = (
  draft: LayoutTemplateDraft,
  layerId: string,
  patch: Partial<LayoutTemplateAiPolicy>,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers.map((layer) =>
        layer.id === layerId
          ? {
              ...layer,
              aiPolicy: sanitizeLayoutTemplateAiPolicy(layer, {
                ...layer.aiPolicy,
                ...patch,
              }),
            }
          : layer,
      ),
    },
  });

export const updateLayoutTemplateLayerAiPolicyField = (
  draft: LayoutTemplateDraft,
  layerId: string,
  field: LayoutTemplatePatchField,
  enabled: boolean,
): LayoutTemplateDraft => {
  const layer = draft.template.canonicalScene.layers.find((item) => item.id === layerId);
  if (!layer) {
    return draft;
  }

  const allowedFields = enabled
    ? Array.from(new Set([...layer.aiPolicy.allowedFields, field]))
    : layer.aiPolicy.allowedFields.filter((item) => item !== field);

  return updateLayoutTemplateLayerAiPolicy(draft, layerId, { allowedFields });
};

export const updateLayoutTemplateLayerOptional = (
  draft: LayoutTemplateDraft,
  layerId: string,
  optional: boolean,
): LayoutTemplateDraft =>
  withTemplate(draft, {
    ...draft.template,
    canonicalScene: {
      ...draft.template.canonicalScene,
      layers: draft.template.canonicalScene.layers.map((layer) => {
        if (layer.id !== layerId) {
          return layer;
        }

        const nextLayer = { ...layer, optional };
        return {
          ...nextLayer,
          aiPolicy: sanitizeLayoutTemplateAiPolicy(nextLayer, nextLayer.aiPolicy),
        };
      }),
    },
  });
